import { useEffect, useMemo, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import MentorPortalService, {
    MentorCreatePairInteractionPayload,
    MentorCreatePairSessionPayload,
    MentorMenteeCard,
} from './MentorPortalService';

type Props = {
    show: boolean;
    mentees: MentorMenteeCard[];
    preselectedPairId: number | null;
    onClose: () => void;
    onLogged: () => void;
};

const MODE_OPTIONS: { mode: string; label: string }[] = [
    { mode: 'ZOOM_TEAMS_MEET', label: 'Zoom/Teams/Google Meet Call' },
    { mode: 'TELEPHONIC', label: 'Telephonic Call' },
    { mode: 'FACETIME_WHATSAPP', label: 'Facetime/WhatsApp conversation' },
    { mode: 'LINKEDIN', label: 'LinkedIn Conversation' },
    { mode: 'IN_PERSON', label: 'In-person Meeting' },
    { mode: 'OTHERS', label: 'Others' },
];

const pad2 = (value: number): string => String(value).padStart(2, '0');

const toDatetimeLocalValue = (date: Date): string =>
    `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}T${pad2(date.getHours())}:${pad2(
        date.getMinutes()
    )}`;

const datetimeLocalToApi = (value: string): string => {
    const trimmed = value.trim();
    if (!trimmed) return trimmed;
    return trimmed.length === 16 ? `${trimmed}:00` : trimmed;
};

const pairLabel = (row: MentorMenteeCard): string =>
    `${row.menteeName?.trim() || 'Mentee'} (pair ${row.pairId})`;

const MentorLogEventModal = ({ show, mentees, preselectedPairId, onClose, onLogged }: Props) => {
    const loggable = useMemo(
        () => mentees.filter((row) => row.canLogOffline === true && row.pairId != null),
        [mentees]
    );

    const [selectedPairId, setSelectedPairId] = useState<number | null>(null);
    const [sessionOccurredAt, setSessionOccurredAt] = useState('');
    const [sessionTitle, setSessionTitle] = useState('');
    const [sessionDuration, setSessionDuration] = useState('');
    const [sessionLink, setSessionLink] = useState('');
    const [interactionOccurredAt, setInteractionOccurredAt] = useState('');
    const [interactionMode, setInteractionMode] = useState('');
    const [interactionNotes, setInteractionNotes] = useState('');
    const [sessionError, setSessionError] = useState('');
    const [interactionError, setInteractionError] = useState('');
    const [sessionSuccess, setSessionSuccess] = useState('');
    const [interactionSuccess, setInteractionSuccess] = useState('');
    const [isSavingSession, setIsSavingSession] = useState(false);
    const [isSavingInteraction, setIsSavingInteraction] = useState(false);

    const nowLocal = toDatetimeLocalValue(new Date());
    const selected = loggable.find((row) => row.pairId === selectedPairId) || null;
    const enabledModes = useMemo(() => {
        const allowed = new Set(selected?.enabledInteractionModes || []);
        return MODE_OPTIONS.filter((option) => allowed.has(option.mode));
    }, [selected]);

    useEffect(() => {
        if (!show) {
            return;
        }
        const now = toDatetimeLocalValue(new Date());
        setSessionOccurredAt(now);
        setInteractionOccurredAt(now);
        setSessionTitle('');
        setSessionDuration('');
        setSessionLink('');
        setInteractionNotes('');
        setSessionError('');
        setInteractionError('');
        setSessionSuccess('');
        setInteractionSuccess('');
        const preferred =
            preselectedPairId != null && loggable.some((row) => row.pairId === preselectedPairId)
                ? preselectedPairId
                : loggable[0]?.pairId ?? null;
        setSelectedPairId(preferred);
    }, [show, preselectedPairId, loggable]);

    useEffect(() => {
        setInteractionMode(enabledModes[0]?.mode ?? '');
    }, [enabledModes, selectedPairId]);

    const canLog = selectedPairId != null && !isSavingSession && !isSavingInteraction;

    const handleLogSession = async () => {
        if (selectedPairId == null) return;
        setSessionError('');
        setSessionSuccess('');
        const occurredAt = datetimeLocalToApi(sessionOccurredAt);
        if (!occurredAt) {
            setSessionError('occurred_at is required');
            return;
        }
        const durationRaw = sessionDuration.trim();
        let durationMinutes: number | undefined;
        if (durationRaw !== '') {
            if (!/^[1-9]\d*$/.test(durationRaw)) {
                setSessionError('duration_minutes must be a positive integer');
                return;
            }
            durationMinutes = Number(durationRaw);
        }
        const payload: MentorCreatePairSessionPayload = { occurredAt };
        const title = sessionTitle.trim();
        const link = sessionLink.trim();
        if (title) payload.title = title;
        if (link) payload.sessionLink = link;
        if (durationMinutes != null) payload.durationMinutes = durationMinutes;
        setIsSavingSession(true);
        try {
            await MentorPortalService.createPairSession(selectedPairId, payload);
            setSessionSuccess('Session logged.');
            setSessionTitle('');
            setSessionDuration('');
            setSessionLink('');
            onLogged();
        } catch (error) {
            setSessionError(MentorPortalService.errorMessage(error));
        } finally {
            setIsSavingSession(false);
        }
    };

    const handleLogInteraction = async () => {
        if (selectedPairId == null) return;
        setInteractionError('');
        setInteractionSuccess('');
        const occurredAt = datetimeLocalToApi(interactionOccurredAt);
        if (!occurredAt) {
            setInteractionError('occurred_at is required');
            return;
        }
        if (interactionMode === '') {
            setInteractionError('mode is required');
            return;
        }
        const payload: MentorCreatePairInteractionPayload = {
            occurredAt,
            mode: interactionMode,
        };
        const notes = interactionNotes.trim();
        if (notes) payload.notes = notes;
        setIsSavingInteraction(true);
        try {
            await MentorPortalService.createPairInteraction(selectedPairId, payload);
            setInteractionSuccess('Interaction logged.');
            setInteractionNotes('');
            onLogged();
        } catch (error) {
            setInteractionError(MentorPortalService.errorMessage(error));
        } finally {
            setIsSavingInteraction(false);
        }
    };

    return (
        <Modal show={show} onHide={onClose} size="lg" centered scrollable>
            <Modal.Header closeButton>
                <Modal.Title>Log interaction</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="mb-3">
                    <label className="form-label" htmlFor="mentor-log-pair">
                        Mentee
                    </label>
                    <select
                        id="mentor-log-pair"
                        className="form-select"
                        value={selectedPairId == null ? '' : String(selectedPairId)}
                        disabled={loggable.length === 0}
                        onChange={(event) => {
                            const next = Number(event.target.value);
                            setSelectedPairId(Number.isFinite(next) && next > 0 ? next : null);
                            setSessionSuccess('');
                            setInteractionSuccess('');
                            setSessionError('');
                            setInteractionError('');
                        }}
                    >
                        {loggable.length === 0 ? (
                            <option value="">No mentees available to log</option>
                        ) : (
                            loggable.map((row) => (
                                <option key={row.pairId as number} value={row.pairId as number}>
                                    {pairLabel(row)}
                                </option>
                            ))
                        )}
                    </select>
                </div>

                <h6 className="font-600">Log a session</h6>
                <div className="row g-2 mb-2">
                    <div className="col-md-6">
                        <label className="form-label" htmlFor="mentor-session-occurred">
                            Occurred at
                        </label>
                        <input
                            id="mentor-session-occurred"
                            type="datetime-local"
                            className="form-control"
                            max={nowLocal}
                            value={sessionOccurredAt}
                            onChange={(event) => setSessionOccurredAt(event.target.value)}
                            disabled={!canLog}
                        />
                    </div>
                    <div className="col-md-6">
                        <label className="form-label" htmlFor="mentor-session-title">
                            Title (optional)
                        </label>
                        <input
                            id="mentor-session-title"
                            type="text"
                            className="form-control"
                            value={sessionTitle}
                            onChange={(event) => setSessionTitle(event.target.value)}
                            disabled={!canLog}
                        />
                    </div>
                    <div className="col-md-6">
                        <label className="form-label" htmlFor="mentor-session-duration">
                            Duration minutes (optional)
                        </label>
                        <input
                            id="mentor-session-duration"
                            type="text"
                            inputMode="numeric"
                            className="form-control"
                            value={sessionDuration}
                            onChange={(event) => setSessionDuration(event.target.value)}
                            disabled={!canLog}
                        />
                    </div>
                    <div className="col-md-6">
                        <label className="form-label" htmlFor="mentor-session-link">
                            Session link (optional)
                        </label>
                        <input
                            id="mentor-session-link"
                            type="url"
                            className="form-control"
                            value={sessionLink}
                            onChange={(event) => setSessionLink(event.target.value)}
                            disabled={!canLog}
                            placeholder="https://"
                        />
                    </div>
                </div>
                {sessionError ? <div className="alert alert-danger py-2">{sessionError}</div> : null}
                {sessionSuccess ? <div className="alert alert-success py-2">{sessionSuccess}</div> : null}
                <Button
                    variant="success"
                    className="mb-3"
                    disabled={!canLog || isSavingSession}
                    onClick={handleLogSession}
                >
                    {isSavingSession ? 'Logging…' : 'Log session'}
                </Button>

                <h6 className="font-600">Log an interaction</h6>
                <div className="row g-2 mb-2">
                    <div className="col-md-6">
                        <label className="form-label" htmlFor="mentor-interaction-occurred">
                            Occurred at
                        </label>
                        <input
                            id="mentor-interaction-occurred"
                            type="datetime-local"
                            className="form-control"
                            max={nowLocal}
                            value={interactionOccurredAt}
                            onChange={(event) => setInteractionOccurredAt(event.target.value)}
                            disabled={!canLog}
                        />
                    </div>
                    <div className="col-md-6">
                        <label className="form-label" htmlFor="mentor-interaction-mode">
                            Mode
                        </label>
                        <select
                            id="mentor-interaction-mode"
                            className="form-select"
                            value={interactionMode}
                            onChange={(event) => setInteractionMode(event.target.value)}
                            disabled={!canLog || enabledModes.length === 0}
                        >
                            {enabledModes.length === 0 ? (
                                <option value="">No enabled modes for this cohort</option>
                            ) : (
                                enabledModes.map((option) => (
                                    <option key={option.mode} value={option.mode}>
                                        {option.label}
                                    </option>
                                ))
                            )}
                        </select>
                    </div>
                    <div className="col-12">
                        <label className="form-label" htmlFor="mentor-interaction-notes">
                            Notes (optional)
                        </label>
                        <textarea
                            id="mentor-interaction-notes"
                            className="form-control"
                            rows={2}
                            value={interactionNotes}
                            onChange={(event) => setInteractionNotes(event.target.value)}
                            disabled={!canLog}
                        />
                    </div>
                </div>
                {interactionError ? <div className="alert alert-danger py-2">{interactionError}</div> : null}
                {interactionSuccess ? (
                    <div className="alert alert-success py-2">{interactionSuccess}</div>
                ) : null}
                <Button
                    variant="success"
                    disabled={!canLog || isSavingInteraction || enabledModes.length === 0}
                    onClick={handleLogInteraction}
                >
                    {isSavingInteraction ? 'Logging…' : 'Log interaction'}
                </Button>
            </Modal.Body>
        </Modal>
    );
};

export default MentorLogEventModal;
